import {AmrapResult, MaxTimeResult, TimeCapResult, Wod, WodType} from "./types";

export const getSavedResults = (): { [key: string]: Wod } => {
    const savedWodsString = localStorage.getItem('wodResults');
    if (savedWodsString) {
        return JSON.parse(savedWodsString);
    }
    return {}
}

export const computeWod = (wod: Wod): Wod => {
    switch (wod.type) {
        case WodType.AMRAP:
            return computeAMRAP(wod);
        case WodType.MAXTIME:
            return computeMaxTime(wod);
        case WodType.TIMECAP:
            return computeTimeCap(wod);
        default:
            return wod;
    }
}

const computeTimeCap = (wod: Wod): Wod => {
    // Sort by time (TIMECAP type)
    const sortedResults = [...wod.results].sort((a, b) => {
        const aResult = a.result as TimeCapResult;
        const bResult = b.result as TimeCapResult;

        // CAP goes to the end
        if (aResult.cap && !bResult.cap) return 1;
        if (!aResult.cap && bResult.cap) return -1;
        if (aResult.cap && bResult.cap) {
            // Both capped, compare by reps (higher is better)
            return (bResult.reps || 0) - (aResult.reps || 0);
        }

        // Compare by time (lower is better)
        const aTime = (aResult.mins || 0) * 60 + (aResult.seconds || 0);
        const bTime = (bResult.mins || 0) * 60 + (bResult.seconds || 0);

        return aTime - bTime;
    });

    // Assign points and ranking with tie handling
    const resultsWithPointsAndRanking = [];
    let currentRanking = 1;

    for (let i = 0; i < sortedResults.length; i++) {
        const teamResult = sortedResults[i];
        const currentResult = teamResult.result as TimeCapResult;

        let ranking = currentRanking;
        let points = 100 - ((currentRanking - 1) * 5);

        // Check for tie with previous result
        if (i > 0) {
            const prevResult = resultsWithPointsAndRanking[i - 1];
            const prevTimeCapResult = prevResult.result as TimeCapResult;

            const isTie =
                currentResult.cap === prevTimeCapResult.cap &&
                (currentResult.cap ?
                    currentResult.reps === prevTimeCapResult.reps :
                    (currentResult.mins || 0) === (prevTimeCapResult.mins || 0) &&
                    (currentResult.seconds || 0) === (prevTimeCapResult.seconds || 0));

            if (isTie) {
                // Use same ranking and points as previous
                ranking = prevResult.ranking;
                points = prevResult.points;
            } else {
                // New ranking position
                currentRanking = i + 1;
                ranking = currentRanking;
                points = 100 - ((currentRanking - 1) * 5);
            }
        }

        resultsWithPointsAndRanking.push({
            ...teamResult,
            ranking,
            points
        });
    }

    return {...wod, results: resultsWithPointsAndRanking};
}

const computeAMRAP = (wod: Wod): Wod => {
    // Sort by reps (AMRAP type) - higher is better
    const sortedResults = [...wod.results].sort((a, b) => {
        const aResult = a.result as AmrapResult;
        const bResult = b.result as AmrapResult;

        return bResult.reps - aResult.reps;
    });

    // Assign points and ranking with tie handling
    const resultsWithPointsAndRanking = [];
    let currentRanking = 1;

    for (let i = 0; i < sortedResults.length; i++) {
        const teamResult = sortedResults[i];
        const currentResult = teamResult.result as AmrapResult;

        let ranking = currentRanking;
        let points = 100 - ((currentRanking - 1) * 5);

        // Check for tie with previous result
        if (i > 0) {
            const prevResult = resultsWithPointsAndRanking[i - 1];
            const prevAmrapResult = prevResult.result as AmrapResult;

            if (currentResult.reps === prevAmrapResult.reps) {
                // Use same ranking and points as previous
                ranking = prevResult.ranking;
                points = prevResult.points;
            } else {
                // New ranking position
                currentRanking = i + 1;
                ranking = currentRanking;
                points = 100 - ((currentRanking - 1) * 5);
            }
        }

        resultsWithPointsAndRanking.push({
            ...teamResult,
            ranking,
            points
        });
    }

    return {...wod, results: resultsWithPointsAndRanking};
}

// For MaxTime WODs where higher time is better (e.g., handstand hold)
const computeMaxTime = (wod: Wod): Wod => {
    // Sort by time (MAXTIME type) - higher is better
    const sortedResults = [...wod.results].sort((a, b) => {
        const aResult = a.result as MaxTimeResult;
        const bResult = b.result as MaxTimeResult;

        // Compare by time (higher is better)
        const aTime = (aResult.mins || 0) * 60 + (aResult.seconds || 0);
        const bTime = (bResult.mins || 0) * 60 + (bResult.seconds || 0);

        return bTime - aTime; // Descending order (higher time is better)
    });

    // Assign points and ranking with tie handling
    const resultsWithPointsAndRanking = [];
    let currentRanking = 1;

    for (let i = 0; i < sortedResults.length; i++) {
        const teamResult = sortedResults[i];
        const currentResult = teamResult.result as MaxTimeResult;

        let ranking = currentRanking;
        let points = 100 - ((currentRanking - 1) * 5);

        // Check for tie with previous result
        if (i > 0) {
            const prevResult = resultsWithPointsAndRanking[i - 1];
            const prevMaxTimeResult = prevResult.result as MaxTimeResult;

            const currentTime = (currentResult.mins || 0) * 60 + (currentResult.seconds || 0);
            const prevTime = (prevMaxTimeResult.mins || 0) * 60 + (prevMaxTimeResult.seconds || 0);

            if (currentTime === prevTime) {
                // Use same ranking and points as previous
                ranking = prevResult.ranking;
                points = prevResult.points;
            } else {
                // New ranking position
                currentRanking = i + 1;
                ranking = currentRanking;
                points = 100 - ((currentRanking - 1) * 5);
            }
        }

        resultsWithPointsAndRanking.push({
            ...teamResult,
            ranking,
            points
        });
    }

    return {...wod, results: resultsWithPointsAndRanking};
}
